export interface TrimmedSample {
  buffer: AudioBuffer;
  trimStartSec: number;
  trimEndSec: number;
}

interface ActiveVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

let audioContext: AudioContext | null = null;
const sampleCache = new Map<string, TrimmedSample>();
const activeVoices = new Set<ActiveVoice>();

const LOOKAHEAD_SEC = 0.06;

export async function getAudioContext(): Promise<AudioContext> {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }
  return audioContext;
}

export function detectTrimPoints(
  buffer: AudioBuffer,
  threshold = 0.012,
): { startSec: number; endSec: number } {
  const channel = buffer.getChannelData(0);
  const rate = buffer.sampleRate;
  const window = Math.max(1, Math.floor(rate * 0.0015));
  const preRoll = Math.floor(rate * 0.001);

  let startSample = 0;
  for (let i = 0; i < channel.length; i += window) {
    let peak = 0;
    const end = Math.min(i + window, channel.length);
    for (let j = i; j < end; j++) {
      peak = Math.max(peak, Math.abs(channel[j] ?? 0));
    }
    if (peak >= threshold) {
      startSample = Math.max(0, i - preRoll);
      break;
    }
  }

  let endSample = channel.length - 1;
  const tailThreshold = threshold * 0.35;
  for (let i = channel.length - window; i >= startSample; i -= window) {
    let peak = 0;
    const end = Math.min(i + window, channel.length);
    for (let j = i; j < end; j++) {
      peak = Math.max(peak, Math.abs(channel[j] ?? 0));
    }
    if (peak >= tailThreshold) {
      endSample = Math.min(channel.length - 1, end);
      break;
    }
  }

  return {
    startSec: startSample / rate,
    endSec: Math.max(startSample + 1, endSample + 1) / rate,
  };
}

export async function loadTrimmedSample(
  key: string,
  url: string,
  threshold = 0.012,
): Promise<TrimmedSample | null> {
  const cached = sampleCache.get(key);
  if (cached) return cached;

  try {
    const ctx = await getAudioContext();
    const res = await fetch(url);
    if (!res.ok) return null;
    const arrayBuffer = await res.arrayBuffer();
    const buffer = await ctx.decodeAudioData(arrayBuffer);
    const { startSec, endSec } = detectTrimPoints(buffer, threshold);
    const trimmed: TrimmedSample = {
      buffer,
      trimStartSec: startSec,
      trimEndSec: endSec,
    };
    sampleCache.set(key, trimmed);
    return trimmed;
  } catch {
    return null;
  }
}

function trackVoice(source: AudioBufferSourceNode, gain: GainNode): void {
  const voice: ActiveVoice = { source, gain };
  activeVoices.add(voice);
  source.addEventListener(
    'ended',
    () => {
      activeVoices.delete(voice);
    },
    { once: true },
  );
}

export function stopAllScheduledVoices(): void {
  for (const voice of activeVoices) {
    try {
      voice.source.stop();
    } catch {
      /* already stopped */
    }
    voice.gain.gain.cancelScheduledValues(0);
    voice.gain.gain.setValueAtTime(0, audioContext?.currentTime ?? 0);
  }
  activeVoices.clear();
}

export async function playSampleNow(
  key: string,
  volume = 0.9,
  maxDurationSec?: number,
): Promise<void> {
  if (!sampleCache.has(key)) return;
  const ctx = await getAudioContext();
  scheduleSample(key, ctx.currentTime + 0.01, volume, maxDurationSec);
}

export function scheduleSample(
  key: string,
  whenSec: number,
  volume = 0.9,
  maxDurationSec?: number,
): AudioBufferSourceNode | null {
  const sample = sampleCache.get(key);
  if (!sample || !audioContext) return null;

  const { buffer, trimStartSec, trimEndSec } = sample;
  const available = trimEndSec - trimStartSec;
  const duration =
    maxDurationSec !== undefined
      ? Math.min(Math.max(maxDurationSec, 0.04), available)
      : available;

  if (duration <= 0) return null;

  const source = audioContext.createBufferSource();
  source.buffer = buffer;

  const gain = audioContext.createGain();
  gain.gain.setValueAtTime(volume, whenSec);

  source.connect(gain);
  gain.connect(audioContext.destination);

  try {
    source.start(whenSec, trimStartSec, duration);
    trackVoice(source, gain);
    return source;
  } catch {
    return null;
  }
}

export function playbackLookaheadSec(): number {
  return LOOKAHEAD_SEC;
}

export async function resolveSampleUrl(candidates: string[]): Promise<string | null> {
  for (const url of candidates) {
    try {
      const res = await fetch(url, { method: 'HEAD' });
      if (res.ok) return url;
    } catch {
      /* try next */
    }
  }
  return null;
}
